import React, { useEffect, useState } from "react";
import { storyblokEditable } from "@storyblok/react";

import { FeatureTablePanelModel } from "./FeatureTablePanelModel";
import { ProductModel } from "../ShowcasePanel/ShowcasePanelModel";
import SliderPanelUtils from "../SliderPanel/SliderPanelUtils";
import Button from "../../core/Button/Button";
import Utils from "../../core/Utils/Utils";

const FeatureTablePanelDesktop: React.FC<{ blok: FeatureTablePanelModel }> = ({
  blok,
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [displayAnnuallyOrMonthlySwitch, setDisplayAnnuallyOrMonthlySwitch] =
    useState(false);
  const [extendedRows, setExtendedRows] = useState<Record<number, boolean>>({});
  const isVideo = blok.media?.filename?.endsWith(".mp4");

  const handleToggle = () => {
    setIsChecked(!isChecked);
  };

  useEffect(() => {
    if (blok?.entity?.some((e) => e.content?.productType === "Subscription")) {
      setDisplayAnnuallyOrMonthlySwitch(true);
    }
    setIsChecked(blok?.defaultSelection === "Monthly");
  }, [blok]);

  if (!blok?.entity || blok.entity.length !== 1) {
    return null;
  }
  const productEntity = blok.entity[0]?.content as ProductModel;

  return (
    <div
      {...storyblokEditable(blok)}
      className="feature-table-panel-container-desktop"
      style={{ color: blok.textColor }}
    >
      {isVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          src={Utils.getCacheUrl(blok.media?.filename)}
          data-object-fit="cover"
          onError={(e) => Utils.handleVideoError(e, blok.media.filename)}
        ></video>
      )}
      {!isVideo && blok.media?.filename && (
        <img
          src={Utils.getCacheUrl(blok.media.filename)}
          alt=""
          onError={(e) => Utils.handleImageError(e, blok.media?.filename)}
        />
      )}

      {blok.title && <h2 className="title h2">{blok.title}</h2>}
      <h3 className="standfirst h3">{blok.standfirst}</h3>

      <div className="feature-table">
        <div className="row header feature-table-product">
          <div className="cell text"></div>
          {blok.items.map((item, index) => {
            return (
              <div
                className="cell"
                key={`feature-table-product-${productEntity.productName}-${index}`}
              >
                {productEntity.productName}
              </div>
            );
          })}
        </div>
        <div className="row header tier feature-table-product">
          <div className="cell text"></div>
          {blok.items.map((item) => {
            return (
              <div
                className="cell"
                key={`feature-table-tier-header-${item.tier}`}
              >
                {item.tier}
              </div>
            );
          })}
        </div>

        <div className="row header">
          <div className="cell text"></div>
          {blok.items.map((item) => {
            const subscriptionIndex = SliderPanelUtils.getSubscriptionIndex(
              productEntity,
              item.tier,
            );
            if (!subscriptionIndex) {
              return "";
            }

            try {
              const nf = new Intl.NumberFormat("en", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });

              let sp = productEntity?.subscriptionIntervalPrice?.tbody;

              let monthlyValue = Number(sp[0]?.body[subscriptionIndex]?.value);
              let annuallyValue = Number(sp[2]?.body[subscriptionIndex]?.value);

              let monthlyFormattedValue: React.ReactNode =
                monthlyValue === 0 ? "" : `${nf.format(monthlyValue)}/month`;
              let annuallyFormattedValue: React.ReactNode = "";

              // if showing yearly prices
              if (!isChecked) {
                annuallyFormattedValue = `${nf.format(annuallyValue)}/year`;

                // if month value is not blank or zero, then need to show calc monthly for comparison
                if (monthlyValue !== 0) {
                  const factor = Math.pow(10, 2);
                  let calcMonthly =
                    Math.trunc((annuallyValue / 12) * factor) / factor;

                  let strikeMonthly = monthlyValue;

                  // if there is an offer, the regular monthly calc moves to the strike, new month calc from offer
                  // and annually line shows strike full annual price with offer price instead
                  let offerValue = Number(
                    sp[3]?.body[subscriptionIndex]?.value,
                  );
                  if (!Number.isNaN(offerValue) && offerValue > 0) {
                    strikeMonthly = calcMonthly;
                    calcMonthly =
                      Math.trunc((offerValue / 12) * factor) / factor;

                    annuallyFormattedValue = (
                      <>
                        <span className="strike">
                          {nf.format(annuallyValue)}
                        </span>{" "}
                        {nf.format(offerValue)}/year
                      </>
                    );
                  }
                  // otherwise, annual line shows just annual price with no strike
                  else {
                    annuallyFormattedValue = `Billed ${annuallyFormattedValue}`;
                  }

                  // always show strike month, values determined above
                  monthlyFormattedValue = (
                    <>
                      <span className="strike">{nf.format(strikeMonthly)}</span>{" "}
                      {nf.format(calcMonthly)}/month
                    </>
                  );
                }
              }

              return (
                <div
                  className="cell"
                  key={`feature-table-tier-header-price-${item.tier}`}
                >
                  {monthlyFormattedValue}&nbsp;
                  <div className="billed">{annuallyFormattedValue}&nbsp;</div>
                </div>
              );
            } catch (ex) {
              return "";
            }
          })}
        </div>

        <div className="row buttons">
          <div className="cell text"></div>
          {blok.items.map((item) => (
            <div className="cell" key={`div-${item._uid}`}>
              {item.buttons.map((button) => {
                if (displayAnnuallyOrMonthlySwitch) {
                  if (isChecked && button.displayFor === "annual") {
                    return null;
                  }
                  if (!isChecked && button.displayFor === "month") {
                    return null;
                  }
                }
                return (
                  <Button blok={button} key={`${button._uid}-${item._uid}`} />
                );
              })}
            </div>
          ))}
        </div>

        {productEntity.featureList?.tbody.map((tbody, index) => {
          const text = tbody.body[0].value;
          const moreInfoIndex = SliderPanelUtils.getFeatureIndex(
            productEntity,
            "More Info",
          );
          const backgroundColorCell =
            index % 2 === 0
              ? blok?.tableEvenRowsColor
              : blok?.tableOddRowsColor;
          return (
            <div key={`feature-table-description-${blok._uid}-${index}`}>
              <div
                className="row"
                onClick={() => {
                  if (tbody.body[moreInfoIndex].value) {
                    setExtendedRows((prev) => ({
                      ...prev,
                      [index]: !prev[index],
                    }));
                  }
                }}
              >
                <div
                  className={`cell text ${
                    tbody.body[moreInfoIndex].value
                      ? extendedRows[index]
                        ? "arrow-up"
                        : "arrow-down"
                      : ""
                  }`}
                  title={text}
                  style={{
                    backgroundColor: backgroundColorCell,
                    opacity: blok.opacity,
                  }}
                >
                  {text}
                </div>
                {blok.items.map((item, itemIndex) => {
                  const featureIndex = SliderPanelUtils.getFeatureIndex(
                    productEntity,
                    item.tier,
                  );
                  const value = tbody.body[featureIndex].value;
                  return (
                    <div
                      className="cell"
                      title={value === "x" ? "" : value}
                      key={`feature-table-description-${blok._uid}-${index}-${itemIndex}`}
                      style={{
                        backgroundColor: backgroundColorCell,
                        opacity: blok.opacity,
                      }}
                    >
                      {value === "x" ? "" : value}
                    </div>
                  );
                })}
              </div>
              <div
                className={`more-info ${extendedRows[index] ? "extended" : ""}`}
                style={{
                  color: blok.extendedRowTextColor,
                  backgroundColor: blok.extendedRowBackgroundColor,
                  opacity: blok.opacity,
                }}
              >
                {tbody.body[moreInfoIndex].value}
              </div>
            </div>
          );
        })}
      </div>
      {displayAnnuallyOrMonthlySwitch && (
        <div className="switch-container">
          <div className="switch-contrtol">
            <span className="slider-label">Annually</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleToggle}
              />
              <span className="slider"></span>
            </label>
            <span className="slider-label">Monthly</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureTablePanelDesktop;
